import React, { Suspense, useMemo, useState, useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Center, Html, Line } from '@react-three/drei';
import { EdgesGeometry, LineSegments, LineBasicMaterial, Vector3, MathUtils } from 'three';
import './bodyvisual.css';

import humanBodyUrl from '../human_body.glb';

function HumanBody({ dim }) {
  const gltf = useGLTF(humanBodyUrl);
  const materialRef = useRef();

  // Setup wireframe on the model once
  useMemo(() => {
    if (!gltf || !gltf.scene || gltf.scene.userData.wireframe) return;

    gltf.scene.traverse((child) => {
      if (child.isMesh) {
        if (child.material) {
          const mat = child.material.clone();
          mat.transparent = true;
          mat.opacity = 0;
          mat.depthWrite = false;
          child.material = mat;
        }

        const edges = new EdgesGeometry(child.geometry, 1.5);
        const material = new LineBasicMaterial({
          color: '#00ccff',
          linewidth: 1,
          transparent: true,
          opacity: 0,
        });
        const line = new LineSegments(edges, material);
        line.renderOrder = 1;
        child.add(line);
      }
    });
    gltf.scene.userData.wireframe = true;
  }, [gltf]);

  // Find the material on mount and reset its opacity
  useEffect(() => {
    gltf.scene.traverse((child) => {
      if (child.isLineSegments) {
        materialRef.current = child.material;
      }
    });

    if (materialRef.current) {
      materialRef.current.opacity = 0;
    }
  }, [gltf.scene]);

  // Animate opacity
  useFrame((state, delta) => {
    if (materialRef.current) {
      const targetOpacity = dim ? 0.2 : 0.45;
      materialRef.current.opacity = MathUtils.lerp(
        materialRef.current.opacity,
        targetOpacity,
        delta * 2.5
      );
    }
  });

  return <primitive object={gltf.scene} scale={1.5} position={[0, 1.2, 0]} />;
}

function EntryAnimation() {
  const { camera, controls } = useThree();

  useEffect(() => {
    const targetPos = new Vector3(2, 3, 4.2);
    const initialPos = new Vector3(2, 4, 8);

    camera.position.copy(initialPos);

    if (controls) {
      controls.enabled = false;
    }

    const animationFrameId = requestAnimationFrame(function animate() {
      camera.position.lerp(targetPos, 0.04);

      if (camera.position.distanceTo(targetPos) > 0.01) {
        requestAnimationFrame(animate);
      } else {
        camera.position.copy(targetPos);
        if (controls) {
          controls.enabled = true;
        }
      }
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (controls) {
        controls.enabled = true;
      }
    };
  }, [camera, controls]);

  return null;
}

// -------- Organ Meshes ---------
function OrganMeshes({ active }) {
  const organs = [
    {
      id: 'heart',
      label: 'Heart',
      position: [0, 1.5, 0],
      geometry: <sphereGeometry args={[0.23, 16, 16]} />,
      labelOffset: [0, 0.35, 0],
    },

    {
      id: 'brain',
      label: 'Brain',
      position: [0, 7.6, -0.05],
      geometry: <sphereGeometry args={[0.26, 10, 10]} />,
      labelOffset: [0.4, 0.3, 0],
    },
    {
      id: 'liver',
      label: 'Liver',
      position: [0.2, 1.2, 0],
      geometry: <boxGeometry args={[0.6, 0.3, 0.4]} />,
      labelOffset: [0.45, 0, 0],
    },
    {
      id: 'kidneys',
      label: 'Kidneys',
      position: [-0.4, 1.1, -0.15],
      geometry: <sphereGeometry args={[0.18, 14, 14]} />,
      labelOffset: [-0.45, 0, 0],
      extra: (
        <mesh position={[0.4, 1.1, -0.15]}>
          <sphereGeometry args={[0.18, 14, 14]} />
          <meshStandardMaterial visible={false} />
        </mesh>
      ),
    },
  ];

  return (
    <group>
      {organs.map((organ) => {
        const isActive = active === organ.id;
        const labelPosition = new Vector3(...organ.position).add(new Vector3(...organ.labelOffset));

        return (
          <group key={organ.id}>
            <mesh position={organ.position} scale={organ.id === 'lungs' ? [1, 1.6, 1.1] : 1}>
              {organ.geometry}
              <meshPhysicalMaterial
                roughness={0.1}
                transmission={1}
                thickness={0.5}
                color={isActive ? '#00bfff' : '#ffffff'}
                transparent
                opacity={isActive ? 0.7 : 0.25}
              />
            </mesh>

            {/* Special second lung mesh */}
            {organ.id === 'lungs' && (
              <mesh
                position={[organ.position[0] + 0.6, organ.position[1], organ.position[2]]}
                scale={[1, 1.6, 1.1]}
              >
                <sphereGeometry args={[0.4, 18, 18]} />
                <meshPhysicalMaterial
                  roughness={0.1}
                  transmission={1}
                  thickness={0.5}
                  color={isActive ? '#00bfff' : '#ffffff'}
                  transparent
                  opacity={isActive ? 0.7 : 0.25}
                />
              </mesh>
            )}

            {isActive && (
              <>
                <Html distanceFactor={7} position={labelPosition.toArray()}>
                  <div className="ONCRP-TOOLS-QP-label">
                    <div className="ONCRP-TOOLS-QP-gene-variant-row">
                      <span className="ONCRP-TOOLS-QP-gene">TP53</span>
                      <span className="ONCRP-TOOLS-QP-variant">p.R273H</span>
                    </div>
                    
                    <div className="ONCRP-TOOLS-QP-organ-count-row">
                      <span className="ONCRP-TOOLS-QP-organ">{organ.label}</span>
                      <span className="ONCRP-TOOLS-QP-disease-count">6</span>
                    </div>
                    <div className="ONCRP-TOOLS-QP-separator"></div>

                    <div className="ONCRP-TOOLS-QP-diseases-list">
                      <div className="ONCRP-TOOLS-QP-disease-item">Glioblastoma</div>
                      <div className="ONCRP-TOOLS-QP-disease-item">Meningioma</div>
                      <div className="ONCRP-TOOLS-QP-disease-item">Astrocytoma</div>
                      <div className="ONCRP-TOOLS-QP-disease-item">Oligodendroglioma</div>
                      <div className="ONCRP-TOOLS-QP-disease-item">Medulloblastoma</div>
                      <div className="ONCRP-TOOLS-QP-disease-item">Ependymoma</div>
                    </div>
                    
                  </div>
                </Html>
                <Line
                  points={[organ.position, [labelPosition.x, labelPosition.y + -0.2, labelPosition.z]]}
                  color="rgba(0, 191, 255, 0.8)"
                  lineWidth={1.5}
                />
                {/* Connection dot */}
                <mesh position={[labelPosition.x, labelPosition.y  + -0.2, labelPosition.z]}>
                  <sphereGeometry args={[0.02, 8, 8]} />
                  <meshBasicMaterial color="#00bfff" />
                </mesh>
              </>
            )}
          </group>
        );
      })}
    </group>
  );
}

function BodyVisual() {
  const [selectedOrgan, setSelectedOrgan] = useState(null);
  return (
    <div className="ONCRP-TOOLS-QP-page">
      <Canvas className="ONCRP-TOOLS-QP-canvas" camera={{ fov: 35 }}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 10, 5]} intensity={1} />

        <Suspense fallback={null}>
          <Center>
            <HumanBody dim={!!selectedOrgan} />
            <OrganMeshes active={selectedOrgan} />
          </Center>
          <EntryAnimation />
        </Suspense>

        <OrbitControls enablePan={false} target={[0, 2.5, 0]} />
      </Canvas>

      {/* Organs panel */}
      <div className="ONCRP-TOOLS-QP-organ-panel">
        {['heart', 'brain', 'liver', 'kidneys'].map((id) => (
          <button
            key={id}
            className={`ONCRP-TOOLS-QP-organ-btn ${selectedOrgan === id ? 'active' : ''}`}
            onClick={() => setSelectedOrgan(selectedOrgan === id ? null : id)}
          >
            {id.charAt(0).toUpperCase() + id.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

export default BodyVisual; 